import { Direction } from '../math/Direction';
import Collection from '../collections/Collection';
import SwipeEventArgs from './SwipeEventArgs';
import SwipeHandlerOptions from './SwipeHandlerOptions';
import Vector2 from '../math/Vector2';
export default class SwipeHandler {
    constructor(allowedDirections, onSwipeStarting, options) {
        this.$maxDistance = 0;
        this.$swiped = false;
        this.touchDiff = new Vector2();
        this.touchMoving = false;
        this.touchStarted = false;
        this.allowedDirections = new Collection(allowedDirections);
        this.onSwipeStarting = onSwipeStarting;
        this.options = new SwipeHandlerOptions(options);
    }
    get maxDistance() { return this.$maxDistance; }
    get currentDirection() { return this.$currentDirection; }
    get swiped() { return this.$swiped; }
    swipe(direction) {
        if (this.startSwipe(direction)) {
            this.completeSwipe(true);
        }
    }
    handleTouchStart(event) {
        var _a;
        if (event.targetTouches.length !== 1 || this.touchStarted) {
            return;
        }
        if ((_a = this.swipeAnimation) === null || _a === void 0 ? void 0 : _a.animationInProgress) {
            this.swipeAnimation.complete();
        }
        this.reset();
        this.touchStart = new Vector2(event.targetTouches[0].screenX, event.targetTouches[0].screenY);
        this.touchStarted = true;
    }
    handleTouchMove(event) {
        if (this.touchStarted) {
            this.touchStarted = false;
            this.touchMoving = true;
        }
        else if (!this.touchMoving) {
            return;
        }
        this.touchDiff = new Vector2(event.targetTouches[0].screenX - this.touchStart.x, event.targetTouches[0].screenY - this.touchStart.y);
        if (!this.validateTouchDiff()) {
            return;
        }
        if (this.touchDiff.x < 0 && this.onSwipeStarting && this.$currentDirection !== Direction.Left) {
            if (!this.startSwipe(Direction.Left)) {
                this.touchDiff.x = 0;
                return;
            }
        }
        else if (this.touchDiff.x > 0 && this.onSwipeStarting && this.$currentDirection !== Direction.Right) {
            if (!this.startSwipe(Direction.Right)) {
                this.touchDiff.x = 0;
                return;
            }
        }
        if (this.touchDiff.x !== 0) {
            event.stopPropagation();
        }
        if (this.swipeAnimation) {
            this.swipeAnimation.setEffectiveProgress(Math.abs(this.touchDiff.x) / this.$maxDistance, this.$swiped);
        }
    }
    handleTouchEnd(event) {
        this.touchStarted = false;
        if (this.touchMoving) {
            this.touchMoving = false;
        }
        else if (!this.touchMoving) {
            return;
        }
        if (!this.$currentDirection) {
            return;
        }
        event.stopPropagation();
        const swipeTreshold = this.$maxDistance * SwipeHandler.SWIPE_ACCEPT_TRESHOLD;
        if (Math.abs(this.touchDiff.x) >= swipeTreshold) {
            this.completeSwipe();
        }
        else {
            this.cancelSwipe();
        }
    }
    validateTouchDiff() {
        if (!this.$currentDirection && this.touchDiff.length >= SwipeHandler.START_DENIAL_LENGTH && Math.abs(this.touchDiff.ratio) < SwipeHandler.START_DENIAL_RATIO) {
            this.reset();
            return false;
        }
        else if (!this.isInBounds() || (!this.$currentDirection && Math.abs(this.touchDiff.x) < SwipeHandler.START_ACCEPT_LENGTH)) {
            this.touchDiff.x = 0;
        }
        if (this.$currentDirection && Math.abs(this.touchDiff.x) > this.$maxDistance) {
            if (this.options.allowOverSwipe) {
                const overswipeDistance = Math.abs(this.touchDiff.x) - this.$maxDistance;
                this.touchDiff.x = (this.$maxDistance + overswipeDistance * SwipeHandler.OVERSWIPE_FACTOR) * (this.$currentDirection === Direction.Right ? 1 : -1);
            }
            else {
                this.touchDiff.x = this.$maxDistance * (this.$currentDirection === Direction.Right ? 1 : -1);
            }
        }
        return true;
    }
    startSwipe(direction) {
        const returnValue = this.onSwipeStarting(this, direction, !this.allowedDirections.contains(direction));
        if (!returnValue) {
            return false;
        }
        this.$maxDistance = returnValue.maxDistance;
        this.swipeAnimation = returnValue.animation;
        this.$currentDirection = direction;
        return true;
    }
    reset() {
        this.touchStarted = false;
        this.touchMoving = false;
        this.swipeAnimation = undefined;
        this.touchDiff.reset();
        this.$currentDirection = undefined;
        if (!this.options.allowReverseSwipe) {
            this.$swiped = false;
        }
    }
    completeSwipe(restartAnimation = false) {
        if (this.$currentDirection === Direction.Left && !this.$swiped) {
            this.swipeAnimation.animateForward({ restart: restartAnimation, restartEasing: true, restartTiming: true });
            this.processCompleting(Direction.Left, false, Direction.Left, true);
        }
        else if (this.$currentDirection === Direction.Left && this.$swiped) {
            this.swipeAnimation.animateReverse({ restart: restartAnimation, restartEasing: true, restartTiming: true });
            this.processCompleting(Direction.Right, true, undefined, false);
        }
        else if (this.$currentDirection === Direction.Right && !this.$swiped) {
            this.swipeAnimation.animateForward({ restart: restartAnimation, restartEasing: true, restartTiming: true });
            this.processCompleting(Direction.Right, false, Direction.Right, true);
        }
        else if (this.$currentDirection === Direction.Right && this.$swiped) {
            this.swipeAnimation.animateReverse({ restart: restartAnimation, restartEasing: true, restartTiming: true });
            this.processCompleting(Direction.Left, true, undefined, false);
        }
    }
    processCompleting(swipeDirection, isReverseSwipe, swipeDirectionState, swipedState) {
        var _a;
        this.$swiped = swipedState;
        this.swipedDirection = swipeDirectionState;
        this.swipeAnimation.options.onCompleted = () => { this.handleAnimationCompleted(swipeDirection, isReverseSwipe); };
        (_a = this.onSwipeCompleting) === null || _a === void 0 ? void 0 : _a.fire(this, new SwipeEventArgs({ direction: swipeDirection, isReverseSwipe: isReverseSwipe }));
    }
    cancelSwipe() {
        if (this.$currentDirection === Direction.Left && !this.$swiped) {
            this.swipeAnimation.animateReverse({ restartEasing: true, restartTiming: true });
            this.processCanceled(Direction.Left, false);
        }
        else if (this.$currentDirection === Direction.Left && this.$swiped) {
            this.swipeAnimation.animateForward({ restartEasing: true, restartTiming: true });
            this.processCanceled(Direction.Right, true);
        }
        else if (this.$currentDirection === Direction.Right && !this.$swiped) {
            this.swipeAnimation.animateReverse({ restartEasing: true, restartTiming: true });
            this.processCanceled(Direction.Right, false);
        }
        else if (this.$currentDirection === Direction.Right && this.$swiped) {
            this.swipeAnimation.animateForward({ restartEasing: true, restartTiming: true });
            this.processCanceled(Direction.Left, true);
        }
        else {
            this.processCanceled(undefined, this.$swiped);
        }
        this.reset();
    }
    processCanceled(swipeDirection, isReverseSwipe) {
        var _a;
        (_a = this.onSwipeCanceled) === null || _a === void 0 ? void 0 : _a.fire(this, new SwipeEventArgs({ direction: swipeDirection, isReverseSwipe: isReverseSwipe }));
        this.$currentDirection = undefined;
    }
    handleAnimationCompleted(swipeDirection, isReverseSwipe) {
        var _a;
        this.reset();
        (_a = this.onSwipeCompleted) === null || _a === void 0 ? void 0 : _a.fire(this, new SwipeEventArgs({ direction: swipeDirection, isReverseSwipe: isReverseSwipe }));
    }
    isInBounds() {
        if (this.touchDiff.x < 0) {
            if (!this.$swiped) {
                return this.allowedDirections.contains(Direction.Left);
            }
            else {
                return this.options.allowReverseSwipe && this.swipedDirection === Direction.Right;
            }
        }
        else if (this.touchDiff.x > 0) {
            if (!this.$swiped) {
                return this.allowedDirections.contains(Direction.Right);
            }
            else {
                return this.options.allowReverseSwipe && this.swipedDirection === Direction.Left;
            }
        }
        else {
            return false;
        }
    }
}
SwipeHandler.START_DENIAL_LENGTH = 10;
SwipeHandler.START_DENIAL_RATIO = 2;
SwipeHandler.START_ACCEPT_LENGTH = 20;
SwipeHandler.OVERSWIPE_FACTOR = 0.25;
SwipeHandler.SWIPE_ACCEPT_TRESHOLD = 0.25;
//# sourceMappingURL=SwipeHandler.js.map